import React, { Component } from 'react'
import { Col, Row } from 'reactstrap'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { Chip, FormControlLabel, Switch, TextField } from '@mui/material'
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton } from '@mui/x-data-grid'
import createPlotlyComponent from 'react-plotly.js/factory'
import Plotly from 'plotly.js-basic-dist-min'
import moment from 'moment'
import KpiCard from '../Report/WeeklySummary/KpiCard'
import { SALE_TYPES, saleTypeLabel, summariseSales, salesTimeline } from '../utils/affiliateCommission'
import { fetchAffiliateSales, filterByDateRange, campaignCodes } from '../utils/affiliateSales'

const Plot = createPlotlyComponent(Plotly)

const eurFormatter = new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' })

const GROSS_COLOR = '#0b8043'
const COMMISSION_COLOR = '#1a73e8'

// Same Finnish presets the reporting time picker offers. That component is bound to the
// reports-get request shape, so only the preset maths is worth reusing here.
const TIME_PRESETS = [
  { label: 'Kampanjan ajalta', value: 'campaign' },
  { label: 'Tämä kuukausi', value: 'thisMonth' },
  { label: 'Viime kk', value: 'lastMonth' },
  { label: '90 pv', value: 'last90' },
  { label: 'Tämä vuosi', value: 'thisYear' },
  { label: 'Kaikki', value: 'all' },
]

const presetRange = (preset, campaign) => {
  const today = moment()
  switch (preset) {
    case 'campaign':
      return { start: (campaign && campaign.start) || '', end: (campaign && campaign.end) || '' }
    case 'thisMonth':
      return { start: today.clone().startOf('month').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') }
    case 'lastMonth':
      return {
        start: today.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        end: today.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      }
    case 'last90':
      return { start: today.clone().subtract(90, 'days').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') }
    case 'thisYear':
      return { start: today.clone().startOf('year').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') }
    default:
      return { start: '', end: '' }
  }
}

const slug = (value) =>
  String(value || '')
    .replace(/[^a-z0-9]/gi, '_')
    .toLowerCase()

function ExportToolbar({ name }) {
  // ';' + utf8WithBom is the house convention - anything else opens as one column in a
  // Finnish Excel.
  return (
    <GridToolbarContainer style={{ justifyContent: 'flex-start' }}>
      <GridToolbarFilterButton />
      <GridToolbarExport
        csvOptions={{ fileName: slug(name) + '-affiliate', delimiter: ';', utf8WithBom: true }}
      />
    </GridToolbarContainer>
  )
}

/**
 * Per-campaign affiliate report: KPIs, breakdown by sale type and by code, a purchase
 * timeline, and the individual attributed sales with CSV export.
 *
 * Every number is computed here from the live sale documents - there are no attribution
 * records - which is why a cancelled sale simply stops counting and a refund needs no
 * handling of its own. The timeline buckets on the PURCHASE date, not on when the court is
 * booked or the event is held.
 */
class AffiliateCampaignReport extends Component {
  constructor(props) {
    super(props)
    const range = presetRange('campaign', props.campaign)
    this.state = {
      loading: true,
      rows: [],
      errors: [],
      preset: 'campaign',
      start: range.start,
      end: range.end,
      showcancelled: false,
    }
  }

  componentDidMount() {
    this.loadSales()
  }

  loadSales = async () => {
    this.setState({ loading: true })
    const { rows, errors } = await fetchAffiliateSales(this.props.firebase, this.props.campaign)
    this.setState({ rows, errors, loading: false })
  }

  applyPreset = (preset) => {
    const range = presetRange(preset, this.props.campaign)
    this.setState({ preset, start: range.start, end: range.end })
  }

  getVisibleRows = () => filterByDateRange(this.state.rows, this.state.start, this.state.end)

  getSummary = () => summariseSales(this.getVisibleRows(), this.props.campaign)

  renderRangeControls = () => (
    <Row className='mb-3'>
      <Col xs='12' className='mb-2'>
        {TIME_PRESETS.map((preset) => (
          <Chip
            key={preset.value}
            label={preset.label}
            clickable
            size='small'
            color='primary'
            variant={this.state.preset === preset.value ? 'filled' : 'outlined'}
            onClick={() => this.applyPreset(preset.value)}
            style={{ marginRight: 4, marginBottom: 4 }}
          />
        ))}
      </Col>
      <Col xs='6' md='2'>
        <TextField
          fullWidth
          size='small'
          type='date'
          label='Alkaen'
          value={this.state.start || ''}
          onChange={(e) => this.setState({ start: e.target.value, preset: 'custom' })}
          InputLabelProps={{ shrink: true }}
        />
      </Col>
      <Col xs='6' md='2'>
        <TextField
          fullWidth
          size='small'
          type='date'
          label='Päättyen'
          value={this.state.end || ''}
          onChange={(e) => this.setState({ end: e.target.value, preset: 'custom' })}
          InputLabelProps={{ shrink: true }}
        />
      </Col>
      <Col xs='12' md='4' className='mt-2'>
        <FormControlLabel
          control={
            <Switch
              size='small'
              checked={this.state.showcancelled}
              onChange={(e) => this.setState({ showcancelled: e.target.checked })}
            />
          }
          label={<span style={{ fontSize: 13 }}>Näytä perutut</span>}
        />
      </Col>
    </Row>
  )

  renderKpis = (summary) => {
    const { total, budget } = summary
    return (
      <Row className='mb-3'>
        <Col xs='12' md='3' className='mb-2'>
          <KpiCard title='Myyntejä' value={total.count} unit='' loading={this.state.loading} />
        </Col>
        <Col xs='12' md='3' className='mb-2'>
          <KpiCard title='Myynti' value={total.gross} unit='€' loading={this.state.loading} />
        </Col>
        <Col xs='12' md='3' className='mb-2'>
          <KpiCard
            title={budget ? 'Palkkio (katto ' + eurFormatter.format(budget.cap) + ')' : 'Palkkio'}
            value={total.commission}
            unit='€'
            loading={this.state.loading}
          />
        </Col>
        <Col xs='12' md='3' className='mb-2'>
          <KpiCard title='Keskiosto' value={total.average} unit='€' loading={this.state.loading} />
        </Col>
      </Row>
    )
  }

  renderTypeChart = (summary) => {
    const labels = SALE_TYPES.map((type) => type.label)
    const gross = SALE_TYPES.map((type) => summary.byType[type.key].gross)
    const commission = SALE_TYPES.map((type) => summary.byType[type.key].commission)

    const traces = [
      {
        x: labels,
        y: gross,
        name: 'Myynti',
        type: 'bar',
        marker: { color: GROSS_COLOR },
        hovertemplate: '%{x}: %{y:,.2f} €<extra>Myynti</extra>',
      },
      {
        x: labels,
        y: commission,
        name: 'Palkkio',
        type: 'bar',
        marker: { color: COMMISSION_COLOR },
        hovertemplate: '%{x}: %{y:,.2f} €<extra>Palkkio</extra>',
      },
    ]

    return this.renderChartCard('Myynti ja palkkio tyypeittäin', traces, {
      barmode: 'group',
      xaxis: { automargin: true, tickfont: { size: 11, color: '#5f6368' } },
      yaxis: { tickfont: { size: 11, color: '#5f6368' }, gridcolor: 'rgba(0,0,0,0.06)', tickformat: ',.0f' },
    })
  }

  renderTimelineChart = (summary) => {
    const timeline = salesTimeline(summary.rows)
    const dates = timeline.dates.map((date) => moment(date, 'YYYY-MM-DD').format('DD.MM.YYYY'))

    const traces = [
      {
        x: dates,
        y: timeline.dailyGross,
        name: 'Myynti / pv',
        type: 'bar',
        marker: { color: '#a5d6b3' },
        hovertemplate: '%{x}: %{y:,.2f} €<extra>Myynti</extra>',
      },
      {
        x: dates,
        y: timeline.cumulativeGross,
        name: 'Kumulatiivinen',
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: GROSS_COLOR, width: 2 },
        yaxis: 'y2',
        hovertemplate: '%{x}: %{y:,.2f} €<extra>Kumulatiivinen</extra>',
      },
    ]

    return this.renderChartCard('Myynti ostopäivän mukaan', traces, {
      xaxis: { type: 'category', automargin: true, tickangle: -25, tickfont: { size: 10, color: '#5f6368' } },
      yaxis: { tickfont: { size: 11, color: '#5f6368' }, gridcolor: 'rgba(0,0,0,0.06)', tickformat: ',.0f' },
      yaxis2: { overlaying: 'y', side: 'right', tickfont: { size: 11, color: '#5f6368' }, tickformat: ',.0f' },
    })
  }

  renderChartCard = (title, traces, layoutExtras) => {
    const hasData = traces.some((trace) => (trace.y || []).some((value) => Number(value) !== 0))
    const layout = {
      autosize: true,
      height: 260,
      margin: { l: 55, r: 55, t: 4, b: 60 },
      showlegend: true,
      legend: { orientation: 'h', x: 0, y: 1.15, font: { size: 11 } },
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      separators: ', ',
      hoverlabel: { bgcolor: '#fff' },
      ...layoutExtras,
    }

    return (
      <Card sx={{ borderRadius: 2, height: '100%' }}>
        <CardContent sx={{ p: '12px 14px', '&:last-child': { pb: '12px' } }}>
          <Typography
            variant='caption'
            sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3 }}
          >
            {title}
          </Typography>
          {this.state.loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260 }}>
              <CircularProgress size={20} />
            </Box>
          ) : !hasData ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 260,
                color: 'text.secondary',
                fontSize: 12,
              }}
            >
              Ei dataa
            </Box>
          ) : (
            <Plot
              data={traces}
              layout={layout}
              config={{ displayModeBar: false, responsive: true }}
              style={{ width: '100%', height: 260 }}
              useResizeHandler
            />
          )}
        </CardContent>
      </Card>
    )
  }

  // Only worth showing when a campaign runs more than one code - that is the whole point of
  // per-channel codes.
  renderCodeBreakdown = (summary) => {
    const codes = campaignCodes(this.props.campaign)
    if (codes.length < 2) return null
    const entries = (this.props.campaign.codes || []).filter((entry) => entry.code)

    return (
      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: '12px 14px', '&:last-child': { pb: '12px' } }}>
          <Typography
            variant='caption'
            sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3 }}
          >
            Koodeittain
          </Typography>
          {entries.map((entry) => {
            const bucket = summary.byCode[String(entry.code).toLowerCase()] || {
              count: 0,
              gross: 0,
              commission: 0,
            }
            return (
              <Row key={entry.code} className='mt-2'>
                <Col xs='4'>
                  <b>{entry.code}</b>
                  {entry.label ? <span style={{ color: '#6c757d' }}> — {entry.label}</span> : null}
                </Col>
                <Col xs='2'>{bucket.count} kpl</Col>
                <Col xs='3'>{eurFormatter.format(bucket.gross)}</Col>
                <Col xs='3'>Palkkio {eurFormatter.format(bucket.commission)}</Col>
              </Row>
            )
          })}
        </CardContent>
      </Card>
    )
  }

  renderBudgetWarning = (summary) => {
    if (!summary.budget || !summary.budget.exceeded) return null
    return (
      <Alert severity='warning' sx={{ mb: 2 }}>
        Palkkiokatto {eurFormatter.format(summary.budget.cap)} on ylittynyt — kertynyt palkkio on{' '}
        {eurFormatter.format(summary.budget.earned)}, josta katon mukaan maksettavaa{' '}
        {eurFormatter.format(summary.budget.payable)}.
      </Alert>
    )
  }

  renderErrors = () => {
    if (this.state.errors.length === 0) return null
    return (
      <Alert severity='error' sx={{ mb: 2 }}>
        Osa myynneistä jäi hakematta ({this.state.errors.join(', ')}). Luvut ovat vajaat.
      </Alert>
    )
  }

  getColDefinitions = () => [
    { field: 'date', headerName: 'Ostopäivä', width: 110 },
    {
      field: 'type',
      headerName: 'Tyyppi',
      width: 130,
      valueGetter: (value, row) => saleTypeLabel(row.type),
    },
    { field: 'sourcename', headerName: 'Kohde', flex: 2, minWidth: 160 },
    { field: 'buyer', headerName: 'Ostaja', flex: 2, minWidth: 150 },
    { field: 'code', headerName: 'Koodi', width: 110 },
    { field: 'quantity', headerName: 'Kpl', width: 70, type: 'number' },
    {
      field: 'gross',
      headerName: 'Myynti',
      width: 110,
      type: 'number',
      valueFormatter: (value) => eurFormatter.format(Number(value) || 0),
    },
    {
      field: 'commission',
      headerName: 'Palkkio',
      width: 110,
      type: 'number',
      valueFormatter: (value) => eurFormatter.format(Number(value) || 0),
    },
    {
      field: 'cancelled',
      headerName: 'Tila',
      width: 100,
      // valueGetter rather than renderCell only, so the CSV carries the word too.
      valueGetter: (value, row) => (row.cancelled ? 'Peruttu' : 'Voimassa'),
    },
  ]

  render() {
    const { campaign } = this.props
    const summary = this.getSummary()
    const gridRows = this.state.showcancelled ? summary.rows : summary.rows.filter((row) => !row.cancelled)

    return (
      <div>
        <Row className='mb-3'>
          <Col>
            <Typography variant='h6'>{campaign.name}</Typography>
            <Typography variant='body2' color='text.secondary'>
              {campaign.affiliatename || '—'} ·{' '}
              {campaignCodes(campaign).join(', ') || 'ei koodeja'}
              {campaign.start || campaign.end
                ? ' · ' + (campaign.start || '…') + ' – ' + (campaign.end || '…')
                : ''}
            </Typography>
          </Col>
          <Col xs='auto'>
            <Button onClick={this.loadSales} disabled={this.state.loading}>
              Päivitä
            </Button>{' '}
            <Button onClick={this.props.onBack}>Palaa listaan</Button>
          </Col>
        </Row>

        {this.renderErrors()}
        {this.renderBudgetWarning(summary)}
        {this.renderRangeControls()}
        {this.renderKpis(summary)}

        <Row className='mb-3'>
          <Col xs='12' md='6' className='mb-2'>
            {this.renderTypeChart(summary)}
          </Col>
          <Col xs='12' md='6' className='mb-2'>
            {this.renderTimelineChart(summary)}
          </Col>
        </Row>

        {this.renderCodeBreakdown(summary)}

        {summary.cancelled.count > 0 && !this.state.showcancelled && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            {summary.cancelled.count} peruttua myyntiä ({eurFormatter.format(summary.cancelled.gross)}) jätetty
            luvuista pois.
          </Typography>
        )}

        <Row>
          <Col xs='12'>
            <div style={{ display: 'flex', minHeight: 420, backgroundColor: '#FFFFFF' }}>
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <DataGrid
                  rows={gridRows}
                  columns={this.getColDefinitions()}
                  loading={this.state.loading}
                  density='compact'
                  showToolbar
                  slots={{ toolbar: ExportToolbar }}
                  slotProps={{ toolbar: { name: campaign.name } }}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  pageSizeOptions={[25, 50, 100]}
                  getRowClassName={(params) => (params.row.cancelled ? 'affiliate-row-cancelled' : '')}
                />
              </div>
            </div>
          </Col>
        </Row>
        <style>{'.affiliate-row-cancelled { color: #adb5bd; text-decoration: line-through; }'}</style>
      </div>
    )
  }
}

export default AffiliateCampaignReport
